import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';
import { dashboardService } from '../api/services/dashboardService';
import { AppIdMetricsDetail, AppIdMetricsOverview } from '../api/types';
import {
  getOverviewTodayDisplay,
  getOverviewYesterdayDisplay,
  getOverviewLastWeekDisplay,
} from '../components/dashboard/utils/versionChartUtils';
import SummaryCardsSkeleton from '../components/skeletons/SummaryCardsSkeleton';
import DetailTableSkeleton from '../components/skeletons/DetailTableSkeleton';
import ChartSkeleton from '../components/skeletons/ChartSkeleton';

interface VersionStat {
  version: string;
  today: number;
  yesterday: number;
  lastWeek: number;
  previousWeek: number;
  total: number;
}

const OpenedSessions: React.FC = () => {
  const navigate = useNavigate();
  const [overviewMetrics, setOverviewMetrics] = useState<AppIdMetricsOverview[]>([]);
  const [metrics, setMetrics] = useState<AppIdMetricsDetail[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hiddenVersions, setHiddenVersions] = useState<Set<string>>(new Set());

  // Helper to render icons with proper typing
  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  // Fetch metrics from API
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        // Fetch overview data for table
        const overviewResponse = await dashboardService.getAppIdMetricsOverview({ metrics_type: 'app_session' });
        setOverviewMetrics(overviewResponse);
        
        // Fetch detailed data for LineChart
        const detailsResponse = await dashboardService.getAppIdMetricsDetails({ metrics_type: 'app_session' });
        setMetrics(detailsResponse);
      } catch (err: any) {
        console.error('Error fetching app session metrics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  // All versions start visible (only top 3 are shown in chart, but user can toggle them)

  // Compare versions semantically (e.g., 3.0.9 > 3.0.8 > 3.0.0 > 2.9.33)
  // Returns: positive if a > b, negative if a < b, 0 if equal
  const compareVersions = (a: string, b: string): number => {
    const parseVersion = (v: string): number[] => {
      return v.split('.').map(num => parseInt(num, 10) || 0);
    };
    
    const aParts = parseVersion(a);
    const bParts = parseVersion(b);
    const maxLength = Math.max(aParts.length, bParts.length);
    
    for (let i = 0; i < maxLength; i++) {
      const aPart = aParts[i] || 0;
      const bPart = bParts[i] || 0;
      if (aPart !== bPart) {
        return aPart - bPart; // If a > b, return positive (a comes first in ascending)
      }
    }
    return 0;
  };

  // Process version stats from overview data
  const processVersionStats = (overviewMetrics: AppIdMetricsOverview[]): VersionStat[] => {
    // Filter for app_session (include "app_id" as well)
    const filtered = overviewMetrics.filter(m => m.metric_type === 'app_session');
    
    const stats = filtered.map(item => ({
      version: item.app_id,
      today: getOverviewTodayDisplay(item),
      yesterday: getOverviewYesterdayDisplay(item),
      lastWeek: getOverviewLastWeekDisplay(item),
      previousWeek: 0, // Not available in overview API
      total: item.total,
    }));

    // Sort: numeric versions first (descending), then "app_id" at the end
    return stats.sort((a, b) => {
      // If one is "app_id", put it at the end
      if (a.version === 'app_id' && b.version !== 'app_id') return 1;
      if (b.version === 'app_id' && a.version !== 'app_id') return -1;
      if (a.version === 'app_id' && b.version === 'app_id') return 0;
      
      // Otherwise use semantic version comparison
      return compareVersions(b.version, a.version); // Descending: highest first
    });
  };

  const versionStats = processVersionStats(overviewMetrics);

  const totals = {
    today: versionStats.reduce((sum, stat) => sum + stat.today, 0),
    yesterday: versionStats.reduce((sum, stat) => sum + stat.yesterday, 0),
    lastWeek: versionStats.reduce((sum, stat) => sum + stat.lastWeek, 0),
    total: versionStats.reduce((sum, stat) => sum + stat.total, 0),
  };

  // Process metrics for LineChart - show all versions
  const processLineChartData = (metrics: AppIdMetricsDetail[]): any[] => {
    if (metrics.length === 0) return [];

    // Get all unique versions
    const allVersions = Array.from(new Set(metrics.map(item => item.app_id)));

    // Group by date
    const dateGroups = metrics.reduce((acc, item) => {
      if (!acc[item.session_date]) {
        acc[item.session_date] = {};
      }
      acc[item.session_date][item.app_id] = item.daily_sessions_count;
      return acc;
    }, {} as Record<string, Record<string, number>>);

    // Get all unique dates and sort
    const dates = Object.keys(dateGroups).sort();
    
    // Take last 30 days
    const last30Days = dates.slice(-30);

    // Build chart data
    return last30Days.map(date => {
      const dataPoint: any = {
        name: new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
        date: date,
      };

      // Add data for all versions
      allVersions.forEach(version => {
        dataPoint[version] = dateGroups[date]?.[version] || 0;
      });

      return dataPoint;
    });
  };

  const lineChartData = processLineChartData(metrics);
  
  // Filter chart data to only show dates where at least one of the top 3 versions has data
  const top3Versions = versionStats.sort((a, b) => b.total - a.total).slice(0, 3).map(stat => stat.version);
  const filteredLineChartData = lineChartData.filter(dataPoint => {
    // Check if at least one of the top 3 versions has data > 0 for this date
    return top3Versions.some(version => (dataPoint[version] || 0) > 0);
  });

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 mb-4 transition-colors"
        >
          {renderIcon(FiArrowLeft, "mr-2")}
          <span>Dashboard'a Dön</span>
        </button>
        <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent mb-2">
          Açılan Oturum Sayısı
        </h1>
        <p className="text-base text-slate-600 dark:text-slate-300">Versiyon bazlı detaylı istatistikler</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">Bugün</p>
          <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
            {totals.today.toLocaleString()}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">Dün</p>
          <p className="text-3xl font-bold bg-gradient-to-r from-sky-600 to-cyan-400 bg-clip-text text-transparent">
            {totals.yesterday.toLocaleString()}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">Son 1 Haftada</p>
          <p className="text-3xl font-bold bg-gradient-to-r from-green-600 to-green-400 bg-clip-text text-transparent">
            {totals.lastWeek.toLocaleString()}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">Toplam</p>
          <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent">
            {totals.total.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white">Versiyon Detayları</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  VERSİYON
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Bugün
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Dün
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Son 1 Haftada
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                  Toplam
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    Yükleniyor...
                  </td>
                </tr>
              ) : versionStats.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    Veri bulunamadı
                  </td>
                </tr>
              ) : (
                versionStats.map((stat, index) => {
                return (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-white">
                      {stat.version}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                      {stat.today.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                      {stat.yesterday.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                      {stat.lastWeek.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-800 dark:text-white">
                      {stat.total.toLocaleString()}
                    </td>
                  </tr>
                );
              })
              )}
              {!loading && versionStats.length > 0 && (
              <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                <td className="px-6 py-4 text-slate-800 dark:text-white">Toplam</td>
                <td className="px-6 py-4 text-slate-800 dark:text-white">{totals.today.toLocaleString()}</td>
                <td className="px-6 py-4 text-slate-800 dark:text-white">{totals.yesterday.toLocaleString()}</td>
                <td className="px-6 py-4 text-slate-800 dark:text-white">{totals.lastWeek.toLocaleString()}</td>
                <td className="px-6 py-4 text-slate-800 dark:text-white">{totals.total.toLocaleString()}</td>
              </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* LineChart */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6">Günlük Gösterim</h2>
        {loading ? (
          <div className="flex items-center justify-center h-96 text-slate-500 dark:text-slate-400">
            Yükleniyor...
          </div>
        ) : lineChartData.length === 0 ? (
          <div className="flex items-center justify-center h-96 text-slate-500 dark:text-slate-400">
            Veri bulunamadı
          </div>
        ) : (
        <>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={filteredLineChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'} />
                <XAxis 
                  dataKey="name" 
                  stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  domain={[0, 'dataMax + 10']} 
                  stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'} 
                />
              <Legend 
                wrapperStyle={{
                  color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827',
                  cursor: 'pointer'
                }}
                onClick={(e: any) => {
                  const version = e.dataKey;
                  if (version) {
                    setHiddenVersions(prev => {
                      const newSet = new Set(prev);
                      if (newSet.has(version)) {
                        newSet.delete(version);
                      } else {
                        newSet.add(version);
                      }
                      return newSet;
                    });
                  }
                }}
                formatter={(value: string) => `Versiyon ${value}`}
              />
              {versionStats.sort((a, b) => b.total - a.total).slice(0, 3).map((stat, index) => {
                const version = stat.version;
                const hue = (index * 137) % 360; // Use a prime number for better color distribution
                const color = `hsl(${hue}, 70%, 50%)`;
                const isHidden = hiddenVersions.has(version);
                return (
                  <Line 
                    key={version}
                    type="monotone" 
                    dataKey={version} 
                    stroke={color} 
                    strokeWidth={2} 
                    dot={{ r: 4 }} 
                    name={version}
                    hide={isHidden}
                    strokeOpacity={isHidden ? 0 : 1}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </>
        )}
      </div>

      {/* Additional Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">En Yüksek Versiyonlar (bugün)</h3>
          <div className="space-y-3">
            {versionStats
              .sort((a, b) => b.today - a.today)
              .slice(0, 5)
              .map((stat, index) => (
                <div key={index} className="flex items-center justify-between gap-2">
                  <div className="flex items-center space-x-3 min-w-0">
                    <span className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300 flex items-center justify-center text-xs font-semibold shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{stat.version}</span>
                  </div>
                  <span className="text-sm font-semibold text-slate-800 dark:text-white shrink-0 text-right tabular-nums">
                    <span className="text-slate-600 dark:text-slate-400 font-normal text-xs block">bugün / dün</span>
                    {stat.today.toLocaleString()} / {stat.yesterday.toLocaleString()}
                  </span>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">Toplam Dağılım</h3>
          <div className="space-y-3">
            {versionStats
              .sort((a, b) => b.total - a.total)
              .slice(0, 5)
              .map((stat, index) => {
                const percentage = (stat.total / totals.total * 100).toFixed(1);
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{stat.version}</span>
                      <span className="text-sm font-semibold text-slate-800 dark:text-white">{percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-indigo-600 to-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenedSessions;

